/**
 * Переходы режимов «Цифры», «Пунктуация» и «Специальные символы».
 *
 * Режимы собирают занятие сами, но mastery и XP считаются теми же функциями,
 * что и после обычной тренировки: результат для пользователя один и тот же, а
 * два разных пути его подсчёта разошлись бы (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  SYMBOL_MODES,
  applySymbolOutcomes,
  buildSymbolsPlan,
  collectSymbolOutcomes,
  hasSymbols,
  type SymbolMode,
  type SymbolsPlan,
  type SymbolsSnapshot,
} from '@type2x/feature-symbols';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/** Снимок навыка для режимов символов. */
export function symbolsSnapshot(workspace: Workspace): SymbolsSnapshot {
  const profile = activeProfile(workspace);

  return {
    language: profile.language,
    layoutId: profile.keyboard.layoutId,
    platform: profile.keyboard.platform,
    stats: profile.stats,
  };
}

/** Режимы символов, которым ещё есть чему учить. */
export function availableSymbolModes(workspace: Workspace | null): SymbolMode[] {
  if (workspace === null) return [];

  const snapshot = symbolsSnapshot(workspace);
  return SYMBOL_MODES.filter((mode) => hasSymbols(snapshot, mode));
}

/** Экран режима со свежим занятием. */
export function symbolsScreen(runtime: Runtime, workspace: Workspace, mode: SymbolMode): AppScreen {
  return {
    name: 'symbols',
    plan: buildSymbolsPlan(
      symbolsSnapshot(workspace),
      mode,
      `${runtime.installationId}:${mode}:${runtime.clock.epochMs()}`,
    ),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Завершает занятие: обновляет символы, начисляет XP и собирает итог. */
export function finishSymbols(
  runtime: Runtime,
  workspace: Workspace,
  plan: SymbolsPlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectSymbolOutcomes(plan, session.keystrokeEvents, session.typingErrors);
  const statsAfter = applySymbolOutcomes(plan.units, profile.stats, outcomes, CONFIG, now);

  // Символ мог встретиться впервые: его единица создаётся вместе с итогом.
  let next = upsertMastery(workspace, plan.units, statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'symbols', mode: plan.mode }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: plan.mode,
      language: profile.language,
      layoutId: profile.keyboard.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.typingErrors,
      isControlled: false,
    }),
  };
}
